import { lazy, Suspense, useState, useEffect } from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { SEOHead } from '@/components/seo/SEOHead';
import { MagicHomePhotosSeo } from '@/components/seo/MagicHomePhotosSeo';
import { MountOnVisible } from '@/components/util/MountOnVisible';
import { LazyBoundary } from '@/components/util/LazyBoundary';
import { lazyRetry } from '@/lib/lazyRetry';

// Everything below the hero fold lives in a separate chunk so framer-motion,
// lucide-icons, Card, Testimonials/Partners bundles etc. stay out of the
// critical Home JS — hero paints while this streams in the background.
const importHomeBelowFold = lazyRetry(() => import('@/components/home/HomeBelowFold'));
const HomeBelowFold = lazy(importHomeBelowFold);

// JsonLdSchema only writes a <script type="application/ld+json"> tag via
// useEffect. Crawlers read the DOM after JS, so a small deferred load is fine
// and keeps SITE_URL/routes helpers out of the hero critical path.
const JsonLdSchema = lazy(
  lazyRetry(() => import('@/components/seo/JsonLdSchema').then(m => ({ default: m.JsonLdSchema })))
);

// DemoRequestBlock is heavy (pulls in zod + supabase client). Lazy-load it so
// it stays out of Home's initial JS chunk — mount only when the dialog opens.
const importDemoRequestBlock = lazyRetry(() =>
  import('@/components/compare/DemoRequestBlock').then((m) => ({ default: m.DemoRequestBlock }))
);
const DemoRequestBlock = lazy(importDemoRequestBlock);



// Hero image served from CDN for LCP optimization (AVIF + WebP responsive)
import hero640Avif from '../../public/images/hero-villa-640-v6.avif.asset.json';
import hero1024Avif from '../../public/images/hero-villa-1024-v6.avif.asset.json';
import hero1920Avif from '../../public/images/hero-villa-1920-v6.avif.asset.json';
import hero640Webp from '../../public/images/hero-villa-640-v6.webp.asset.json';
import hero1024Webp from '../../public/images/hero-villa-1024-v6.webp.asset.json';
import hero1920Webp from '../../public/images/hero-villa-1920-v6.webp.asset.json';
const heroAvifSrcSet = `${hero640Avif.url} 640w, ${hero1024Avif.url} 1024w, ${hero1920Avif.url} 1920w`;
const heroWebpSrcSet = `${hero640Webp.url} 640w, ${hero1024Webp.url} 1024w, ${hero1920Webp.url} 1920w`;
const heroVilla = hero1920Webp.url;

const seoContent = {
  en: { title: 'i-Rent.net | Holiday Rental Management Software', description: 'Professional PMS & Channel Manager for holiday rental agencies. Next-generation booking engine to get more bookings across 50+ channels.' },
  es: { title: 'i-Rent.net | Software de Gestión de Alquiler Vacacional', description: 'PMS y Channel Manager para agencias de alquiler vacacional. Motor de reservas de última generación para más reservas en más de 50 canales.' },
  de: { title: 'i-Rent.net | Ferienvermietungs-Software', description: 'Professionelles PMS & Channel Manager für Ferienvermietungsagenturen. Buchungsmaschine der neuesten Generation für mehr Buchungen über 50+ Kanäle.' },
  fr: { title: 'i-Rent.net | Logiciel PMS pour Locations de Vacances', description: 'PMS & Channel Manager professionnel pour agences de locations de vacances. Moteur de réservation de dernière génération pour plus de réservations sur 50+ canaux.' },
  nl: { title: 'i-Rent.net | Professionele Vakantieverhuur Software', description: 'Professioneel PMS & Channel Manager voor vakantieverhuuragentschappen. Boekingsmotor van de nieuwste generatie voor meer boekingen via 50+ kanalen.' },
  it: { title: 'i-Rent.net | Software PMS per Affitti Vacanze', description: 'PMS & Channel Manager professionale per agenzie di affitti vacanze. Motore di prenotazione di ultima generazione per più prenotazioni su 50+ canali.' },
  pt: { title: 'i-Rent.net | Software PMS para Alugueres de Férias', description: 'PMS & Channel Manager profissional para agências de alugueres de férias. Motor de reservas de última geração para mais reservas em 50+ canais.' },
  dk: { title: 'i-Rent.net | Professionel Ferieudlejningssoftware', description: 'Professionelt PMS & Channel Manager til ferieudlejningsbureauer. Bookingmotor af nyeste generation for flere bookinger via 50+ kanaler.' }
};

export default function Home() {
  const { t, language } = useLanguage();
  const [demoOpen, setDemoOpen] = useState(false);
  // Defer below-fold + JSON-LD until the browser is idle so hero LCP paints
  // without competing for main-thread work with off-screen JS.
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    const ric: (cb: () => void) => number =
      (window as any).requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 200));
    const id = ric(() => setDeferredReady(true));
    return () => {
      const cic = (window as any).cancelIdleCallback || window.clearTimeout;
      cic(id);
    };
  }, []);
  const currentSeo = seoContent[language as keyof typeof seoContent] || seoContent.en;
  return <>
      {/* Home hero LCP preload lives statically in index.html so the browser
          can start the AVIF fetch during HTML parse, before hydration. */}
      <SEOHead title={currentSeo.title} description={currentSeo.description} />
      <MagicHomePhotosSeo />

      {deferredReady && (
        <LazyBoundary>
          <JsonLdSchema
            type="software"
            name="i-Rent.net"
            description={currentSeo.description}
            applicationCategory="BusinessApplication"
            language={language}
          />
        </LazyBoundary>
      )}


      {/* Hero Section */}
      <section
        className="relative overflow-hidden min-h-[90vh] flex items-center"
        aria-labelledby="hero-heading"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <picture>
            <source
              type="image/avif"
              srcSet={heroAvifSrcSet}
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet={heroWebpSrcSet}
              sizes="100vw"
            />

            <img
              src={heroVilla}
              alt="Luxury Mediterranean holiday villa with private pool, palm trees and sea view — representative of properties managed with i-Rent.net"
              className="w-full h-full object-cover object-[70%_center] md:object-center animate-slow-zoom"
              width={1920}
              height={1440}
              fetchPriority="high"
              decoding="sync"
            />
          </picture>
          <div
            className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-background/30"
            aria-hidden="true"
          />
        </div>

        <div className="container relative z-10 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex bg-primary/10 backdrop-blur-sm text-primary pl-3 pr-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="mr-2 flex-shrink-0" aria-hidden="true">☁️</span>
              <span>{t('hero.cloudBadge').replace('☁️ ', '')}</span>
            </div>
            <h1
              id="hero-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
            >
              {t('hero.title')}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
                <DialogTrigger asChild>
                  <Button
                    size="lg"
                    aria-label={`${t('hero.cta')} — ${t('hero.title')}`}
                    onMouseEnter={importDemoRequestBlock}
                    onFocus={importDemoRequestBlock}
                  >
                    {t('hero.cta')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
                  <DialogTitle className="sr-only">{t('hero.cta')}</DialogTitle>
                  <DialogDescription className="sr-only">{t('hero.title')}</DialogDescription>
                  {demoOpen && (
                    <LazyBoundary
                      suspenseFallback={<div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
                    >
                      <DemoRequestBlock source="hero-cta" />
                    </LazyBoundary>
                  )}
                </DialogContent>
              </Dialog>
              <Button size="lg" variant="outline" className="bg-background/50 backdrop-blur-sm" asChild>
                <LocalizedLink
                  routeKey="features"
                  aria-label={`${t('hero.ctaSecondary')} — ${t('hero.title')}`}
                >
                  {t('hero.ctaSecondary')}
                </LocalizedLink>
              </Button>
            </div>
          </div>
        </div>
      </section>


      {/* Below-fold is mounted only when the user scrolls close to it.
          IntersectionObserver keeps the main thread free during hero-LCP
          even when the browser reports "idle" while the image is still
          decoding — more stable than a plain requestIdleCallback trigger. */}
      <MountOnVisible rootMargin="600px 0px" prefetchRootMargin="1500px 0px" prefetch={importHomeBelowFold} minHeight="50vh">
        <LazyBoundary sectionId="home-belowfold" suspenseFallback={<div style={{ minHeight: '50vh' }} />}>
          <HomeBelowFold />
        </LazyBoundary>

      </MountOnVisible>
    </>;
}
